import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const OPTION_SOURCE_DEFINITIONS = {
  "work-history": {
    getUrl: ({ apiBase, userId }) => `${apiBase}/api/work-history/${userId}`,
    normalizeRows: (rows) =>
      rows.filter((row) => String(row.wh_name || "").trim()),
    toOption: (row) => ({
      value: String(row.wh_id ?? ""),
      label: row.wh_name || "",
      record: row,
    }),
  },
  equipment: {
    getUrl: ({ apiBase }) => `${apiBase}/api/dropdowns/equip-operated`,
    toOption: (row) => {
      const value =
        row.eo_name || row.label || row.value || row.name || "";
      return { value: String(value), label: value, record: row };
    },
  },
  "road-testers": {
    getUrl: ({ apiBase }) => `${apiBase}/api/dropdowns/road-testers`,
    toOption: (row) => ({
      value: String(row.id ?? row.value ?? ""),
      label: row.name || row.label || row.value || "",
      record: row,
    }),
  },
  "lc-managers": {
    getUrl: ({ apiBase }) => `${apiBase}/api/dropdowns/lc-managers`,
    toOption: (row) => ({
      value: String(row.id ?? row.value ?? ""),
      label: row.name || row.label || row.value || "",
      record: row,
    }),
  },
  "safety-supervisors": {
    getUrl: ({ apiBase }) => `${apiBase}/api/dropdowns/safety-supervisors`,
    toOption: (row) => ({
      value: String(row.name || row.label || row.value || ""),
      label: row.name || row.label || row.value || "",
      record: row,
    }),
  },
};

export const getCustomTaskSelectOptions = (source, rows = []) => {
  const definition = OPTION_SOURCE_DEFINITIONS[source];
  if (!definition) return [];

  const options = rows
    .map(definition.toOption)
    .filter((option) => option.value !== "" || option.label !== "");

  if (source === "road-testers") {
    options.push({ value: "__other__", label: "Other", record: null });
  }

  return options;
};

const useCustomTaskOptionSources = ({
  apiBase,
  token,
  userId,
  sources = [],
}) => {
  const [sourceRows, setSourceRows] = useState({});
  const sourceKey = useMemo(
    () => [...new Set(sources)].sort().join("|"),
    [sources],
  );

  useEffect(() => {
    let cancelled = false;
    const requestedSources = sourceKey
      .split("|")
      .filter((source) => OPTION_SOURCE_DEFINITIONS[source]);

    if (!userId || requestedSources.length === 0) {
      setSourceRows({});
      return undefined;
    }

    Promise.all(
      requestedSources.map(async (source) => {
        const definition = OPTION_SOURCE_DEFINITIONS[source];

        try {
          const response = await axios.get(
            definition.getUrl({ apiBase, userId }),
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const rows = Array.isArray(response.data) ? response.data : [];
          return [
            source,
            definition.normalizeRows
              ? definition.normalizeRows(rows)
              : rows,
          ];
        } catch (error) {
          console.error(`Failed to load custom task options: ${source}`, error);
          return [source, []];
        }
      }),
    ).then((entries) => {
      if (!cancelled) {
        setSourceRows(Object.fromEntries(entries));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [apiBase, sourceKey, token, userId]);

  return sourceRows;
};

export default useCustomTaskOptionSources;
